/**
 * Pre-Snap Modifier Factory
 *
 * Creates path split modifiers for pre-snap motion/shift indicators.
 * Pre-snap markers split drawing paths into two portions:
 * - Pre-snap: Always dashed (motion before snap)
 * - Post-snap: Normal (motion) or dashed (shift)
 */

import type { PlayBlock_Union } from '@/types/blocks'
import type { ControlPointBlock } from '@/types/blocks/canvas/ControlPoint'
import type { PreSnapMarkerBlock } from '@/types/blocks/canvas/PreSnapMarker'
import type { PathSplitModifier } from './Types'
import type { Point } from '@/components/blocks/Types'

/**
 * Get snap point position from resolved control points
 *
 * Looks up the control point matching snapPointId from the resolved children
 * and extracts its (x, y) position.
 */
function getSnapPointPosition(
	snapPointId: number | string,
	resolvedControlPoints?: ControlPointBlock[]
): Point {
	if (!resolvedControlPoints || resolvedControlPoints.length === 0) {
		console.warn('No resolved control points available for snap point lookup')
		return { x: 0, y: 0 }
	}

	const snapPointIdStr = String(snapPointId)
	const controlPoint = resolvedControlPoints.find(
		(cp) => String(cp.id) === snapPointIdStr
	)

	if (!controlPoint) {
		console.warn(`Snap point ${snapPointId} not found in resolved control points`)
		return { x: 0, y: 0 }
	}

	const { x, y } = controlPoint.properties as { x?: number; y?: number }
	return { x: x ?? 0, y: y ?? 0 }
}

/**
 * Creates a path split modifier from a pre-snap marker block
 *
 * @param preSnapMarker - The pre-snap marker child block
 * @param resolvedControlPoints - Resolved control point blocks from the parent drawing
 * @returns PathSplitModifier configuration
 *
 * @example
 * ```typescript
 * const preSnapMarker = findChildByType(drawingBlock, 'pre_snap_marker')
 * const modifier = createPreSnapModifier(preSnapMarker, resolvedControlPoints)
 *
 * // modifier.type === 'path_split'
 * // modifier.preSnapStyle === 'dashed'
 * // modifier.postSnapStyle === 'normal' (motion) or 'dashed' (shift)
 * ```
 */
export function createPreSnapModifier(
	preSnapMarker: PlayBlock_Union,
	resolvedControlPoints?: ControlPointBlock[]
): PathSplitModifier {
	// Type guard
	if (preSnapMarker.type !== 'pre_snap_marker') {
		throw new Error(
			`createPreSnapModifier called with non-pre_snap_marker block: ${preSnapMarker.type}`
		)
	}

	const marker = preSnapMarker as PreSnapMarkerBlock
	const { snapPointId, motionType } = marker.properties

	if (snapPointId === undefined) {
		throw new Error('Pre-snap marker must have a snapPointId')
	}

	return {
		type: 'path_split',
		splitPointId: snapPointId,
		preSnapStyle: 'dashed',
		postSnapStyle: motionType === 'shift' ? 'dashed' : 'normal',
		indicator: {
			position: getSnapPointPosition(snapPointId, resolvedControlPoints),
			motionType: motionType,
		},
	}
}
